import { ViewTransition } from "react";

import { resolveProjectCoverTransitionName } from "@/music-catalog/utils/project-cover-transition";
import { PROJECT_COVER_TRANSITION_TYPE } from "@/navigation/constants/transition-types";

import type { ReactElement } from "react";

/**
 * A project's Front cover, carried from the page it was on to the page it opens.
 *
 * Every place that draws a Front wraps it in this under a name derived from the
 * project, so opening an album from the catalog or the home page moves its cover
 * into the project page rather than swapping one picture for another. It
 * animates because navigation runs inside `startTransition`, and React animates
 * nothing outside one.
 *
 * Shared only under `PROJECT_COVER_TRANSITION_TYPE`, which `TransitionTypeRouter`
 * adds to a navigation with a project page on either end. The name alone would
 * pair every album two pages both draw, so going from the home page to the
 * catalog would fly every cover the two have in common across.
 *
 * Shared and nothing else: `default="none"` keeps a cover from fading on its own
 * when its page goes and no page after it draws the same album, so leaving a
 * catalog of sixty albums does not capture sixty pictures. The timing and the
 * reduced-motion answer are `@ceedero/ui`'s `theme.css`, not this component's.
 *
 * The child is the cover's own element with nothing between: React names the
 * first DOM node beneath it, and a wrapper would carry the name instead. Only a
 * Front goes in — a Disc or a Back must not fly into the cover's place.
 */
export const ProjectCoverTransition = ({
  children,
  desktopProjectId,
}: {
  children: ReactElement;
  desktopProjectId: string;
}) => (
  <ViewTransition
    name={resolveProjectCoverTransitionName(desktopProjectId)}
    default="none"
    share={{
      [PROJECT_COVER_TRANSITION_TYPE]: "auto",
      default: "none",
    }}
  >
    {children}
  </ViewTransition>
);
